/**
 * Calculate which image to display based on mouse position
 */

export class AngleCalculator {
  private gridSize: number;

  constructor(gridSize = 9) {
    this.gridSize = gridSize;
  }

  /**
   * Calculate image index based on mouse position relative to container
   * @param mouseX Mouse X position (page coordinates)
   * @param mouseY Mouse Y position (page coordinates)
   * @param containerRect Container bounding rectangle
   * @returns Image index (0 to gridSize*gridSize - 1)
   */
  public calculateImageIndex(
    mouseX: number,
    mouseY: number,
    containerRect: DOMRect
  ): number {
    // Calculate container center
    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;

    // Calculate delta from center
    const deltaX = mouseX - centerX;
    const deltaY = mouseY - centerY;

    // Calculate distance from center
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // If mouse is very close to center, return center image
    const centerThreshold = 30; // pixels
    if (distance < centerThreshold) {
      return this.getCenterIndex();
    }

    // Calculate angle in radians (-PI to PI)
    const angle = Math.atan2(deltaY, deltaX);

    // Convert to degrees (0 to 360)
    let degrees = (angle * 180) / Math.PI;
    if (degrees < 0) degrees += 360;

    // Map angle and distance to grid position
    return this.angleToGridIndex(degrees, distance);
  }

  /**
   * Get the center image index
   */
  private getCenterIndex(): number {
    const center = Math.floor(this.gridSize / 2);
    return center * this.gridSize + center;
  }

  /**
   * Map angle and distance to grid index
   * Grid layout (9x9 example):
   * 0  1  2  3  4  5  6  7  8
   * 9  10 11 12 13 14 15 16 17
   * ...
   * 36 37 38 39 40 41 42 43 44  (40 is center)
   * ...
   * 72 73 74 75 76 77 78 79 80
   */
  private angleToGridIndex(degrees: number, distance: number): number {
    const center = Math.floor(this.gridSize / 2);

    // Determine how far from center (1 to center)
    // For simplicity, we'll use 3 distance levels for a 9x9 grid
    const maxDistance = 3; // Maximum steps from center
    const distanceLevel = Math.min(
      Math.ceil((distance / 200) * maxDistance),
      maxDistance
    );

    // Divide 360 degrees into 8 directions
    const directions = 8;
    const degreesPerDirection = 360 / directions;

    // Calculate direction index (0-7)
    // 0: Right (0°), 1: Down-Right (45°), 2: Down (90°), etc.
    const directionIndex = Math.floor(
      ((degrees + degreesPerDirection / 2) % 360) / degreesPerDirection
    );

    // Map direction to row/col offset
    const directionOffsets = [
      { row: 0, col: 1 }, // Right (0°)
      { row: 1, col: 1 }, // Down-Right (45°)
      { row: 1, col: 0 }, // Down (90°)
      { row: 1, col: -1 }, // Down-Left (135°)
      { row: 0, col: -1 }, // Left (180°)
      { row: -1, col: -1 }, // Up-Left (225°)
      { row: -1, col: 0 }, // Up (270°)
      { row: -1, col: 1 }, // Up-Right (315°)
    ];

    const offset = directionOffsets[directionIndex];

    // Calculate target row and col
    const targetRow = center + offset.row * distanceLevel;
    const targetCol = center + offset.col * distanceLevel;

    // Clamp to grid bounds
    const row = Math.max(0, Math.min(this.gridSize - 1, targetRow));
    const col = Math.max(0, Math.min(this.gridSize - 1, targetCol));

    // Convert to index
    return row * this.gridSize + col;
  }

  /**
   * Alternative: Use the ratio-based approach from the grid data
   * This matches the original implementation more closely
   */
  public calculateImageIndexByRatio(
    mouseX: number,
    mouseY: number,
    containerRect: DOMRect,
    images: Array<{ ratio: { xRatio: number; yRatio: number } }>
  ): number {
    // Calculate container center
    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;

    // Calculate normalized position (-1 to 1)
    const normalizedX = (mouseX - centerX) / (containerRect.width / 2);
    const normalizedY = (mouseY - centerY) / (containerRect.height / 2);

    // Clamp to -1 to 1
    const clampedX = Math.max(-1, Math.min(1, normalizedX));
    const clampedY = Math.max(-1, Math.min(1, normalizedY));

    // Find closest image by ratio
    let closestIndex = Math.floor(images.length / 2); // Default to center
    let minDistance = Number.POSITIVE_INFINITY;

    images.forEach((img, index) => {
      const dx = img.ratio.xRatio - clampedX;
      const dy = img.ratio.yRatio - clampedY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }
}
